import React, { useEffect, useState } from 'react';
import { CalendarClock, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../api/examApi';
import { parseApiError } from '../../lib/apiErrors';

const LEVEL_BADGE = {
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  soft: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};
const LEVEL_LABEL = { overdue: 'Overdue', soft: 'Due Soon' };

// Admin-only. Two independent actions in one panel: setting the next-due
// date and recording a payment — neither depends on the other completing.
export function StudentFeePanel({ studentId }) {
  const [nextDue, setNextDue] = useState('');
  const [feeLevel, setFeeLevel] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDue, setSavingDue] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.getStudent(studentId), adminApi.listPayments(studentId)])
      .then(([studentRes, paymentsRes]) => {
        setNextDue(studentRes.data.next_payment_due || '');
        setFeeLevel(studentRes.data.fee_level || null);
        setPayments(paymentsRes.data);
      })
      .catch(() => toast.error('Failed to load fee data'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [studentId]);

  const handleSaveDue = async (e) => {
    e.preventDefault();
    setSavingDue(true);
    try {
      const { data } = await adminApi.setNextDue(studentId, nextDue || null);
      setNextDue(data.next_payment_due || '');
      setFeeLevel(data.fee_level || null);
      toast.success('Next due date updated');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to update next due date').message);
    } finally {
      setSavingDue(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    setSavingPayment(true);
    try {
      await adminApi.recordPayment(studentId, { amount: parsedAmount, received_on: receivedOn, note: note || undefined });
      toast.success('Payment recorded');
      setAmount('');
      setNote('');
      load();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to record payment').message);
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        Fees
      </h3>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Next due date */}
        <form onSubmit={handleSaveDue} className="space-y-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Next Payment Due
            {feeLevel && (
              <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${LEVEL_BADGE[feeLevel]}`}>
                {LEVEL_LABEL[feeLevel]}
              </span>
            )}
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={nextDue}
              onChange={(e) => setNextDue(e.target.value)}
              className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="next-due-input"
            />
            <button
              type="submit"
              disabled={savingDue || loading}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 font-medium text-white hover:bg-primary-light disabled:opacity-50"
              data-testid="save-next-due-btn"
            >
              <CalendarClock size={16} />
              {savingDue ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>

        {/* Record payment */}
        <form onSubmit={handleRecordPayment} className="space-y-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Record Payment</label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="payment-amount-input"
            />
            <input
              type="date"
              value={receivedOn}
              onChange={(e) => setReceivedOn(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="payment-date-input"
            />
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            data-testid="payment-note-input"
          />
          <button
            type="submit"
            disabled={savingPayment}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="record-payment-btn"
          >
            <IndianRupee size={16} />
            {savingPayment ? 'Recording…' : 'Record Payment'}
          </button>
        </form>
      </div>

      <div className="mt-6">
        <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Payment History</p>
        {loading ? (
          <div className="h-16 animate-pulse rounded-md bg-slate-100 dark:bg-slate-700" />
        ) : payments.length === 0 ? (
          <p className="text-sm text-slate-400">No payments recorded yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/50 text-left text-slate-500">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {payments.map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{p.received_on}</td>
                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">₹{p.amount}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{p.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
